/* eslint-disable max-lines-per-function */
import * as crypto from 'crypto';
import * as http from 'http';
import * as https from 'https';
import { URL } from 'url';
import * as query from 'querystring';
import * as zlib from 'zlib';
import { getRandomInt } from '@deltasierra/utilities-math';
import { assertNever } from '../common/lang/errors';

/*
 * This file has been re-created based on:
 * https://github.com/Kong/mashape-oauth
 *
 * Modified by Jordan McDonough - 28/09/2020
 *
 * Notes:
 * The repository was old and had not been updated in
 * the last 4 years. Since we were only using the OAuth1
 * component of the package, and small components, we
 * decided to pull the package into our own repo for
 * easier maintainence and typing.
 *
 * I have rewritten the file and stripped out any old
 * unnecessary or unrelated code for our purposes.
 * I also updated any deprecated API's and added typings.
 *
 * Some functions are now missing, as we didn't use them,
 * therefore spending time maintaining and updating them
 * would be a waste of time.
 * If you need them, refer back to the mashape-oauth repo
 * and add them back as necessary, they shouldn't be too
 * hard to include back in here.
 */

type RequestHeaders = Record<string, number | string | undefined>;

type ParameterableType = Record<string, boolean | number | string | undefined>;

interface GetSignatureOptions {
    method: OAuth1HttpMethods;
    url: string;
    parameters: Partial<OAuth1Parameters>;
    tokenSecret?: string;
}

export type OAuth1HttpMethods = 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';

// Some valid parameters for OAuth1 requests
export interface OAuth1Parameters extends ParameterableType {
    oauth_body_hash?: string;
    oauth_consumer_key: string;
    oauth_nonce: string;
    oauth_signature_method: string;
    oauth_timestamp: number;
    oauth_token?: string;
    oauth_token_secret?: string;
    oauth_verifier?: string;
    oauth_version: string;
}

export type WithSignedParameters = {
    oauth_signature: string;
};

export type SignedParameterableType = ParameterableType & WithSignedParameters;

export type ExtraParameters = ParameterableType;

export interface CustomRequestOptions {
    additionalParameters?: Partial<OAuth1Parameters>;
    body?: Buffer | string;
    contentType?: string;
    method?: OAuth1HttpMethods;
    oAuthToken?: string;
    oAuthTokenSecret?: string;
    url: string;
}

export type CompleteRequestOptions = CustomRequestOptions &
    Required<Pick<CustomRequestOptions, 'method'>> & {
        body?: string;
    };

export type RequestOptionsWithToken = Required<Pick<CustomRequestOptions, 'oAuthToken' | 'oAuthTokenSecret'>>;

export type PerformSecureRequestResult = {
    data: string;
    response: http.IncomingMessage;
    statusCode?: number;
};

export type PerformSecureRequestOptions = CustomRequestOptions & Required<Pick<CustomRequestOptions, 'url'>>;

export type OAuth1AccessTokenOptions = Pick<OAuth1Parameters, 'oauth_verifier'> & RequestOptionsWithToken;

export type OAuth1ResponseResults = {
    oauth_callback_confirmed?: string;
};

export type OAuth1AccessTokenResponse = {
    token: string;
    secret: string;
    results: OAuth1ResponseResults;
    response: http.IncomingMessage;
    statusCode?: number;
};

export type OAuth1RequestTokenResponse = {
    token: string;
    secret: string;
    results: OAuth1ResponseResults;
    response: http.IncomingMessage;
    statusCode?: number;
};

export type OAuth1AuthHeaderOptions = CustomRequestOptions & RequestOptionsWithToken;

export type SignaturePlaintext = {
    signatureMethod: 'PLAINTEXT';
};

export type SignatureRSA = {
    signatureMethod: 'RSA-SHA1';
    privateKey: string;
};

export type SignatureHMAC = {
    signatureMethod: 'HMAC-SHA1';
};

// Create a discriminating union based on the signatue method
export type OAuth1SignatureType = SignatureHMAC | SignaturePlaintext | SignatureRSA;

export type OAuth1Options = OAuth1SignatureType & {
    accessTokenHttpMethod?: OAuth1HttpMethods;
    accessTokenUrl: string;
    requestTokenCallback?: string;
    consumerKey: string;
    consumerSecret: string;
    headerParamSeperator?: string;
    headers?: RequestHeaders;
    nonceLength?: number;
    requestTokenHttpMethod?: OAuth1HttpMethods;
    requestTokenUrl: string;
    version?: string;
};

export type OAuth1Settings = Required<OAuth1Options>;

// Converts the optional request options to the complete request options
function toCompleteOptions(options: CustomRequestOptions): CompleteRequestOptions {
    let body: string | undefined;
    if (options.body && Buffer.isBuffer(options.body)) {
        body = options.body.toString();
    } else {
        body = options.body;
    }
    return {
        method: 'GET',
        ...options,
        body,
    };
}

/*
 * Copied the encode data command from mashape-oauth
 * This function replaces unencoded parts of strings
 * In the url with the Correct encoded url value
 * E.g: ! = %21
 * ' = %27
 */
function encodeData(data: boolean | number | string) {
    if (data === '' || !data) {
        return '';
    }
    return encodeURIComponent(data)
        .replace(/!/g, '%21')
        .replace(/'/g, '%27')
        .replace(/\(/g, '%28')
        .replace(/\)/g, '%29')
        .replace(/\*/g, '%2A');
}

/*
 * Calculates current timestamp in seconds.
 * Utilizes flooring to prevent being sent too soon.
 */
function getTimestamp() {
    return Math.floor(new Date().getTime() / 1000);
}

/*
 * Nonce Character Table.
 * By Default this character table is `a-zA-Z0-9`.
 */
const NONCE_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

function generateNonce(length = 32): string {
    const result = [];

    for (let i = 0; i < length; i++) {
        result.push(NONCE_CHARS.charAt(getRandomInt(0, NONCE_CHARS.length)));
    }

    return result.join('');
}

function isAnEarlyCloseHost(hostname: string) {
    return hostname && (/.*google(apis)?.com$/).exec(hostname);
}

function queryStringValuesToString(parsedQuery: query.ParsedUrlQuery): Record<string, string> {
    return Object.keys(parsedQuery)
        .map(key => ({
            [key]: queryStringValueToString(parsedQuery[key] ?? ''),
        }))
        .reduce((prev, cur) => ({ ...prev, ...cur }), {});
}

function queryStringValueToString(str: string[] | string) {
    return typeof str === 'string' ? str : str.join(',');
}

// Converts a list of parameters to an encoded string for the url
function convertParametersToString(parameters: ParameterableType, joiner = '&', wrapValuesWithQuotes = false): string {
    const valueWrapper = wrapValuesWithQuotes ? '"' : '';
    return (
        Object.keys(parameters)
            // Sort the keys by name
            .sort()
            /*
             * Map the keys to either:
             * Arrays: array = [1, 2, 3] to array=1&array=2&array=3
             * Single values: string = 'abc' to string=abc
             */
            .map(key => {
                const value = parameters[key];
                if (value === undefined) {
                    return undefined;
                }

                if (Array.isArray(value)) {
                    return value
                        .map(arrayValue => `${encodeData(key)}=${valueWrapper}${encodeData(arrayValue)}${valueWrapper}`)
                        .join(joiner);
                } else {
                    return `${encodeData(key)}=${valueWrapper}${encodeData(value)}${valueWrapper}`;
                }
            })
            // Filter out any undefined values
            .filter(value => value !== undefined)
            /*
             * And then finally join them all in order:
             * To end up with: array=1&array=2&array=3&string=abc
             */
            .join(joiner)
    );
}

/*
 * Not sure if this function is required, however we will include it
 * As it was included in the original file
 */
function normalizeUrl(url: string) {
    const parsed = new URL(url);
    let port = '';

    if (parsed.port) {
        if (
            (parsed.protocol === 'http:' && parsed.port !== '80') ||
            (parsed.protocol === 'https:' && parsed.port !== '443')
        ) {
            port = `:${parsed.port}`;
        }
    }

    return `${parsed.protocol}//${parsed.hostname}${port}${parsed.pathname === '' ? '/' : parsed.pathname}`;
}

type HttpRequestOptions = http.RequestOptions & {
    ssl: false;
};

type HttpsRequestOptions = https.RequestOptions & {
    ssl: true;
};

function createClient(options: HttpRequestOptions | HttpsRequestOptions) {
    return (options.ssl ? https : http).request(options);
}

export class OAuth1 {
    private settings: OAuth1Settings;

    public constructor(options: OAuth1Options) {
        this.settings = {
            accessTokenHttpMethod: 'POST',
            headerParamSeperator: ',',
            headers: {
                Accept: '*/*',
                Connection: 'close',
                'User-Agent': 'Gatekeeper-OAUTH',
            },

            nonceLength: 32,
            requestTokenCallback: 'oob',
            requestTokenHttpMethod: 'POST',
            version: '1.0',
            ...options,

            consumerSecret: encodeData(options.consumerSecret),
        };
    }

    public async getOAuthRequestToken(): Promise<OAuth1RequestTokenResponse> {
        const { requestTokenCallback, requestTokenHttpMethod, requestTokenUrl } = this.settings;

        const { data, response, statusCode } = await this.performSecureRequest(
            toCompleteOptions({
                additionalParameters: {
                    oauth_callback: requestTokenCallback,
                },
                method: requestTokenHttpMethod,
                url: requestTokenUrl,
            }),
        );

        const {
            oauth_token: token,
            oauth_token_secret: secret,
            ...results
        } = queryStringValuesToString(query.parse(data));

        return {
            response,
            results,
            secret,
            statusCode,
            token,
        };
    }

    public async getOAuthAccessToken({
        oAuthToken,
        oAuthTokenSecret,
        ...otherTokenOptions
    }: OAuth1AccessTokenOptions): Promise<OAuth1AccessTokenResponse> {
        const { accessTokenHttpMethod, accessTokenUrl } = this.settings;

        const { data, response, statusCode } = await this.performSecureRequest(
            toCompleteOptions({
                additionalParameters: {
                    ...otherTokenOptions,
                },
                method: accessTokenHttpMethod,
                oAuthToken,
                oAuthTokenSecret,
                url: accessTokenUrl,
            }),
        );

        const {
            oauth_token: token,
            oauth_token_secret: secret,
            ...results
        } = queryStringValuesToString(query.parse(data));

        return {
            response,
            results,
            secret,
            statusCode,
            token,
        };
    }

    public authHeader(options: OAuth1AuthHeaderOptions): string {
        return this.buildAuthorizationHeaders(this.prepareAndSignParametersForRequest(toCompleteOptions(options)));
    }

    public async get(requestOptions: CustomRequestOptions): Promise<PerformSecureRequestResult> {
        return this.performSecureRequest(toCompleteOptions({ ...requestOptions, method: 'GET' }));
    }

    public async post(requestOptions: CustomRequestOptions): Promise<PerformSecureRequestResult> {
        return this.performSecureRequest(toCompleteOptions({ ...requestOptions, method: 'POST' }));
    }

    private async performSecureRequest(options: CompleteRequestOptions): Promise<PerformSecureRequestResult> {
        const headers: RequestHeaders = {
            ...this.settings.headers,
        };

        const parsedUrl = new URL(options.url);

        const isSSLRequired = parsedUrl.protocol !== 'http:';
        const host = parsedUrl.host;
        // Ensure the correct port is being used if it is not already set
        const port = parsedUrl.port || (isSSLRequired ? '443' : '80');

        headers.Host = host || undefined;

        let urlQueryString: string;
        const preparedParams = this.prepareAndSignParametersForRequest(options);
        // If we are getting the access url and GET, do not use the Authorization header, instead use query
        if (options.url === this.settings.accessTokenUrl && this.settings.accessTokenHttpMethod === 'GET') {
            urlQueryString = this.buildAuthorizationQuery(preparedParams);
        } else {
            headers.Authorization = this.buildAuthorizationHeaders(preparedParams);
        }

        /*
         * If the options type is not set
         * Prevent setting default content-type for methods that generally disallow
         * It to appease APIs such as JIRA.
         */
        if (options.method !== 'GET' && !options.contentType) {
            options.contentType = 'application/x-www-form-urlencoded';
        }

        if (options.contentType) {
            headers['Content-Type'] = options.contentType;
        }

        /*
         * If the body is set, we set it to either the buffer length, or the bytelength of the string
         * Otherwise we set it to 0
         */
        headers['Content-Length'] = options.body ? options.body.length : 0;

        // Correct the parsedUrl pathname
        if (!parsedUrl.pathname || parsedUrl.pathname === '') {
            parsedUrl.pathname = '/';
        }

        return new Promise<PerformSecureRequestResult>((resolve, reject) => {
            const request = createClient({
                headers,
                host,
                method: options.method,
                // If the parsedUrl query is not null, we will
                path: urlQueryString ? `${parsedUrl.pathname}?${urlQueryString}` : parsedUrl.pathname,
                port,
                ssl: isSSLRequired,
            });

            const earlyClose = host ? isAnEarlyCloseHost(host) : false;

            type GZipPayload = {
                type: 'gzip';
                data: Buffer;
            };

            type DefaultPayload = {
                type: 'default';
                data: string;
            };

            let payload: DefaultPayload | GZipPayload;

            let called = false;

            const respond = (response: http.IncomingMessage) => {
                // Prevent calling more than once
                if (called) {
                    return;
                }

                called = true;

                if (payload.type === 'gzip') {
                    // Convert to string payload
                    payload = {
                        data: payload.data.toString('utf8'),
                        type: 'default',
                    };
                }

                if (response.statusCode === undefined) {
                    reject(new Error('Response status code is undefined'));
                    return;
                }

                if (response.statusCode >= 200 && response.statusCode <= 299) {
                    resolve({
                        data: payload.data,
                        response,
                    });
                } else if (
                    (response.statusCode === 301 || response.statusCode === 302) &&

                    response?.headers?.location
                ) {
                    /*
                     * If the response is moved, and they have supplied a location perform the request on the new
                     * locations
                     */
                    this.performSecureRequest({
                        ...options,
                        url: response.headers.location,
                    })
                        .then(resolve)
                        .catch(reject);
                } else {
                    // Otherwise we should probably resolve an error but the previous function didnt so eh
                    resolve({
                        data: payload.data,
                        response,
                        statusCode: response.statusCode,
                    });
                }
            };

            request.on('response', (response: any) => {
                let output: http.IncomingMessage | zlib.Gunzip;

                if (response.headers['content-encoding'] === 'gzip') {
                    const gunzip = zlib.createGunzip();
                    payload = {
                        data: Buffer.alloc(0),
                        type: 'gzip',
                    };
                    response.pipe(gunzip);
                    output = gunzip;
                } else {
                    response.setEncoding('utf8');
                    payload = {
                        data: '',
                        type: 'default',
                    };
                    output = response;
                }

                output.on('data', (chunk: any) => {
                    if (payload.type === 'gzip') {
                        payload.data = Buffer.concat([payload.data, chunk]);
                    } else {
                        payload.data += chunk;
                    }
                });

                output.on('close', () => {
                    if (earlyClose) {
                        respond(response);
                    }
                });

                output.on('end', () => {
                    respond(response);
                });
            });

            request.on('error', (error: any) => {
                called = true;
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                reject(error);
            });

            if (options.method !== 'GET' && options.body) {
                request.write(options.body);
            }

            request.end();
        });
    }

    private buildAuthorizationHeaders(parameters: SignedParameterableType): string {
        // Only include the oauth_ parameters in the auth header
        const oAuthOnlyParams: ParameterableType = {};
        for (const key of Object.keys(parameters)) {
            if ((/^oauth_/).exec(key)) {
                oAuthOnlyParams[key] = parameters[key];
            }
        }
        return `OAuth ${convertParametersToString(oAuthOnlyParams, this.settings.headerParamSeperator, true)}`;
    }

    private buildAuthorizationQuery(parameters: SignedParameterableType) {
        return convertParametersToString(parameters, '&');
    }

    private prepareAndSignParametersForRequest(options: CompleteRequestOptions): SignedParameterableType {
        const oAuthParameters = this.prepareOAuthParametersForRequest(options);

        let bodyParameters: ParameterableType = {
            ...oAuthParameters,
        };

        // If we have not hashed the body we must pass the body params
        if (!oAuthParameters.oauth_body_hash && options.body) {
            if (options.contentType && options.contentType.toLowerCase() === 'application/x-www-form-urlencoded') {
                bodyParameters = {
                    ...bodyParameters,
                    ...queryStringValuesToString(query.parse(options.body)),
                };
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                bodyParameters = {
                    ...bodyParameters,
                    ...JSON.parse(options.body),
                };
            }
        }

        const parsedUrl = new URL(options.url);

        // If we have any query params in the url we add them to the body params

        if (parsedUrl.searchParams) {
            bodyParameters = {
                ...bodyParameters,
                ...parsedUrl.searchParams,
            };
        }

        return this.signParameters(options, bodyParameters);
    }

    private prepareOAuthParametersForRequest({
        additionalParameters,
        oAuthToken,
        oAuthTokenSecret,
    }: CompleteRequestOptions): OAuth1Parameters {
        return {
            oauth_consumer_key: this.settings.consumerKey,
            oauth_nonce: generateNonce(this.settings.nonceLength),
            oauth_signature_method: this.settings.signatureMethod,
            oauth_timestamp: getTimestamp(),
            oauth_token: oAuthToken,
            oauth_token_secret: oAuthTokenSecret,
            oauth_version: this.settings.version,
            ...additionalParameters,
        };
    }

    private signParameters<T extends ParameterableType>(
        { method, oAuthTokenSecret, url }: CompleteRequestOptions,
        parameters: T,
    ): T & WithSignedParameters {
        return {
            ...parameters,
            oauth_signature: this.getSignature({
                method,
                parameters,
                tokenSecret: oAuthTokenSecret,
                url,
            }),
        };
    }

    private getSignature(options: GetSignatureOptions) {
        return this.createSignature(options);
    }

    private createSignatureBase({
        method,
        parameters,
        url,
    }: Pick<GetSignatureOptions, 'method' | 'parameters' | 'url'>): string {
        return `${method.toUpperCase()}&${encodeData(normalizeUrl(url))}&${encodeData(
            convertParametersToString(parameters),
        )}`;
    }

    private createSignature({ method, parameters, tokenSecret, url }: GetSignatureOptions) {
        const base = this.createSignatureBase({ method, parameters, url });
        const key = `${this.settings.consumerSecret}&${encodeData(tokenSecret || '')}`;

        if (this.settings.signatureMethod === 'PLAINTEXT') {
            return key;
        } else if (this.settings.signatureMethod === 'RSA-SHA1') {
            return crypto
                .createSign('RSA-SHA1')
                .update(base)
                .sign(this.settings.privateKey || '', 'base64');

        } else if (this.settings.signatureMethod === 'HMAC-SHA1') {
            return crypto.createHmac('sha1', key).update(base).digest('base64');
        } else {
            throw assertNever(this.settings);
        }
    }
}
